import { cva } from 'class-variance-authority';
import { CarouselImageVariants, CarouselSectionTypes, CarouselTextVariants } from './types';
const outerWrapperVariants = cva('grid grid-cols-[1.5rem_1fr_1.5rem] pt-12 md:pt-14 xl:pt-[3.75rem]', {
    variants: {
        variant: {
            light: 'md:grid-cols-[4rem_10.5rem_1fr]',
            dark: 'md:bg-brand-navy md:grid-cols-[4rem_auto_1fr_14.25rem]',
            images: 'bg-brand-navy-2 text-white md:grid-cols-[4rem_40%_1fr] xl:grid-cols-[6.25rem_min(32rem,40%)_1fr_16rem] md:grid-rows-[auto_12.5rem_auto_1fr]',
        },
    },
    compoundVariants: [
        {
            variant: ['light', 'dark'],
            className: 'xl:grid-cols-[6.25rem_auto_1fr_16rem]',
        },
    ],
});
const textContainerVariants = cva('row-start-1 col-start-2 md:col-end-4', {
    variants: {
        variant: {
            light: 'xl:max-w-3xl',
            dark: 'max-w-5xl md:text-white',
            images: 'text-white xl:max-w-lg xl:col-end-3 xl:row-end-3 md:mb-8 xl:mb-14 xl:mr-10',
        },
    },
    compoundVariants: [
        {
            variant: ['light', 'dark'],
            className: 'text-brand-navy',
        },
        {
            variant: ['light', 'images'],
            className: 'max-w-2xl',
        },
    ],
});
const subtitleVariants = cva('uppercase text-xl font-bold tracking-wider block mb-4', {
    variants: {
        variant: {
            light: 'text-brand-navy-2',
            dark: 'text-brand-navy-2 md:text-brand-navy-3',
            images: 'text-brand-navy-4',
        },
    },
});
const titleVariants = cva('text-[32px] font-medium', {
    variants: {
        variant: {
            default: 'leading-[1.3] xl:text-[40px]',
            images: 'md:text-5xl',
        },
    },
});
const descriptionVariants = cva('tracking-tight mt-4', {
    variants: {
        variant: {
            default: 'md:text-xl md:mt-3.5 xl:mt-4',
            images: 'md:text-2xl md:mt-6',
        },
    },
});
const ctaContainerVariants = cva('col-start-2 self-start', {
    variants: {
        variant: {
            default: 'row-start-2',
            images: 'z-10 row-start-5 md:hidden',
        },
        hasCtas: {
            true: 'flex flex-wrap items-center',
        },
        gapSize: {
            default: '',
            sm: '',
        },
    },
    compoundVariants: [
        {
            variant: 'default',
            hasCtas: true,
            className: 'mt-8 xl:mt-10 xl:mr-[6.25rem] md:col-end-4 xl:col-end-3',
        },
        {
            variant: 'default',
            hasCtas: false,
            className: 'md:w-[7.5rem] xl:w-[11.25rem]',
        },
        {
            variant: 'images',
            hasCtas: true,
            className: 'my-10',
        },
        {
            variant: 'images',
            hasCtas: false,
            className: 'h-10',
        },
        {
            hasCtas: true,
            gapSize: 'default',
            className: 'gap-y-4 gap-x-6 md:gap-x-8',
        },
        {
            hasCtas: true,
            gapSize: 'sm',
            className: 'gap-3.5',
        },
    ],
});
const navigationContainerVariants = cva('flex gap-3.5 col-start-2 xl:mt-0 xl:mx-14 xl:row-start-1 xl:col-start-4 xl:self-end', {
    variants: {
        variant: {
            light: 'row-start-3 mt-10 md:mt-24',
            dark: 'md:mx-[2.625rem] mt-6 md:mt-0 md:row-start-1 md:col-start-4 md:self-end',
            images: 'row-start-2 mt-12 md:mt-16 mb-8 md:mb-[4.5rem] xl:mb-10',
        },
    },
});
const navigationButtonVariants = cva('flex', {
    variants: {
        variant: {
            light: '',
            dark: 'md:text-white md:border-white',
            images: 'text-white border-white',
        },
    },
    compoundVariants: [
        {
            variant: ['light', 'dark'],
            className: 'text-brand-navy border-brand-navy',
        },
    ],
});
const carouselWrapperVariants = cva('carousel-wrapper z-10 min-w-0 col-start-2 md:col-start-3 col-end-4 xl:col-end-5 gap-8', {
    variants: {
        variant: {
            light: 'md:row-start-3',
            dark: 'md:row-start-2 md:col-end-5',
            images: 'row-start-3 md:row-start-2 md:row-end-4 flex flex-col items-start justify-start md:mb-10 xl:mb-14',
        },
    },
    compoundVariants: [
        {
            variant: ['light', 'dark'],
            className: 'h-full row-start-4 xl:row-start-2',
        },
    ],
});
const carouselVariants = cva('[&_.swiper-slide]:h-auto', {
    variants: {
        variant: {
            light: '!py-10 xl:!pb-14',
            dark: '!pt-6 !pb-12 md:!pt-12 xl:!pb-14',
            images: '',
        },
        sliderType: {
            [CarouselSectionTypes.Product]: '[&_.swiper-slide]:bg-brand-grey [&_.swiper-slide]:w-[252px] md:[&_.swiper-slide]:w-[325px]',
            [CarouselSectionTypes.Image]: '[&_.swiper-slide]:w-[322px] [&_.swiper-slide-active_.active-border]:opacity-100',
            [CarouselSectionTypes.Education]: '[&_.swiper-slide]:w-[315px]',
        },
    },
    compoundVariants: [
        {
            variant: ['light', 'dark'],
            className: '!pr-6',
        },
    ],
});
const imageCarouselTextVariants = cva('text-brand-navy z-10 row-start-4 md:row-start-3 md:row-end-5 col-start-2 md:mb-10 xl:mb-14', {
    variants: {
        variant: {
            [CarouselImageVariants.Simple]: 'mt-3.5 md:mt-12 xl:mt-20 max-w-lg md:mr-6 xl:mr-10 md:self-end',
            [CarouselImageVariants.Sidenav]: 'mt-2 md:mt-8 xl:mt-[1.125rem] space-y-2 max-w-xs md:mr-10',
        },
    },
});
const deckButtonVariants = cva('transition-all border-brand-navy', {
    variants: {
        isSelected: {
            true: 'border-b-[3px] mb-0',
            false: 'opacity-40 border-b-[1px] mb-[2px]',
        },
    },
});
const carouselBgWrapperVariants = cva('relative col-start-1 col-end-[none] pointer-events-none', {
    variants: {
        variant: {
            light: 'md:row-start-3 xl:row-start-2',
            dark: 'md:row-start-2',
            images: 'row-start-3 row-end-6 md:row-end-5',
        },
    },
    compoundVariants: [
        {
            variant: ['light', 'dark'],
            className: 'row-start-4',
        },
    ],
});
const carouselBgVariants = cva('absolute bottom-0 left-0 w-full', {
    variants: {
        variant: {
            light: 'h-[calc(100%-11.375rem)] md:h-[calc(100%-12.25rem)] xl:h-[calc(100%-11.5rem)]',
            dark: 'bg-[#F5F7F8] md:bg-white h-[calc(100%-11.25rem)] md:h-[calc(100%-14.25rem)] xl:h-[calc(100%-13.25rem)]',
            images: 'bg-[#E6F5FF] h-[calc(100%-12.5rem)] md:h-full',
        },
    },
});
export const carouselTiledVariants = {
    outerWrapperVariants,
    carouselWrapperVariants,
    carouselVariants,
    deckButtonVariants,
    ctaContainerVariants,
    navigationButtonVariants,
    navigationContainerVariants,
    descriptionVariants,
    titleVariants,
    subtitleVariants,
    textContainerVariants,
    carouselBgWrapperVariants,
    carouselBgVariants,
    imageCarouselTextVariants,
};
const slideContainerVariants = cva('max-w-[1335px] px-6 md:px-12 xl:px-[60px] mt-6 md:mt-[60px] mb-4 mx-auto h-full w-full relative', {
    variants: {
        textVariant: {
            [CarouselTextVariants.Quote]: 'flex flex-col justify-between pt-2 md:pt-0',
            [CarouselTextVariants.Content]: 'xl:pt-5 h-full md:pb-28',
        },
    },
});
export const carouselFullWidthVariants = {
    slideContainerVariants,
};
